import { useCallback, useEffect, useState } from 'react'
import FileDropzone from 'admin/FileDropzone'
import ConfirmModal from 'common/ConfirmModal'
import Alert from 'common/notifications/Alert'
import DropDownMenu from 'common/forms/DropDown'
import closeIcon from 'images/icon-close.svg'
import iconError from 'images/iconColorinfo.svg'
import successIcon from 'images/TickMark.svg'
import { parse } from "csv-parse/browser/esm/sync";
import {
  checkNewEiarFiles,
  deleteNewEiarFile,
  getNewEiarStatus,
  publishNewEiar,
  uploadEiarSpreadsheet,
} from '../mggadminutil'
import { FilledBlue, FilledYellow } from 'css/Buttons'
import * as colors from 'css/Colors'
import { getBrochurePreSignedUrl } from '../../reports/documentsapi'
import BrochureUploadButton from './BrochureUploadButton'
import css from '../sass/upload.documents.module.scss'
import confirmModalCss from '../sass/confirm.modal.module.scss'
import deleteIcon from '../../images/trash-can-icon.png'

const testReportYears = localStorage.isSeleniumTest === 'true' ? ['1969'] : []
export const reportYears = ['2028', '2027', '2026', ...testReportYears]
const reportYearOptions = ['Select', ...reportYears]

const getSpreadsheetYear = (file, selectedReportYear) => {
  const year = file.name.substring(0, 4);
  if (!/^\d{4}$/.test(year) || !reportYears.includes(year)) {
    throw new Error(`EIAR spreadsheet filename must begin with one of these report years: ${reportYears.join(', ')}`);
  }
  if (year !== selectedReportYear) {
    throw new Error(`The selected report year is ${selectedReportYear}, but the EIAR spreadsheet filename begins with ${year}. Select ${year} or rename the file so the years match.`);
  }

  return year;
}

const uploadSpreadsheetFile = async (args, selectedReportYear) => {
  const file = args[0];

  if (!file) return;

  const year = getSpreadsheetYear(file, selectedReportYear);
  const csv = await file.text();
  const reports = parse(csv, {
    columns: true,
    skip_empty_lines: true,
    trim: true,
  });

  const result = reports.map((row, index) => {
    const crmId = row["CRM ID"];
    if (typeof crmId !== 'string' || !/^\d{10}$/.test(crmId)) {
      throw new Error(`CRM ID in CSV row ${index + 2} must be a 10-digit string`);
    }

    return {
      crmId,
      lastName: row["Last Name"],
      firstName: row["First Name"],
      fileNames: row["File Names"]
        .split(",")
        .map(s => s.trim()),
    };
  });

  return await uploadEiarSpreadsheet(year, result);
}

const hasBlockingDiscrepancies = result => (
  result
  && (result.spreadsheetEntriesNotInFiles.length > 0
    || result.ambiguousSpreadsheetEntries.length > 0)
)

const hasAnyDiscrepancies = result => (
  result
  && (result.filesNotInSpreadsheet.length > 0
    || result.spreadsheetEntriesNotInFiles.length > 0
    || result.ambiguousSpreadsheetEntries.length > 0)
)

const formatPublishDate = publishDate => {
  const date = new Date(publishDate)
  return Number.isNaN(date.getTime()) ? publishDate : date.toLocaleString()
}

export const fullName = "New EIAR Reports"
export const uploadTitle = "New EIAR (Endowment in Action) Reports"

export const ReportContent = ({
  reportYear,
  setReportYear,
  newEiarFileUploadCount,
  clearFileUploadResults,
}) => {
  const [spreadsheetUploaded, setSpreadsheetUploaded] = useState(false)
  const [spreadsheetExists, setSpreadsheetExists] = useState(false)
  const [spreadsheetFiles, setSpreadsheetFile] = useState([])
  const [spreadsheetError, setSpreadsheetError] = useState('')
  const [spreadsheetSuccess, setSpreadsheetSuccess] = useState('')
  const [spreadsheetSaving, setSpreadsheetSaving] = useState(false)
  const [spreadsheetDropzoneKey, setSpreadsheetDropzoneKey] = useState(0)
  const [showSpreadsheetConfirmModal, setShowSpreadsheetConfirmModal] = useState(false)
  const [publishDate, setPublishDate] = useState('')
  const [discrepancies, setDiscrepancies] = useState(null)
  const [statusLoading, setStatusLoading] = useState(false)
  const [extraFileToDelete, setExtraFileToDelete] = useState('')
  const [hasEiarBrochure, setHasEiarBrochure] = useState(null)

  useEffect(() => {
    let current = true

    setSpreadsheetUploaded(false)
    setSpreadsheetExists(false)
    setSpreadsheetFile([])
    setSpreadsheetError('')
    setSpreadsheetSuccess('')
    setSpreadsheetDropzoneKey(key => key + 1)
    setShowSpreadsheetConfirmModal(false)
    setPublishDate('')
    setDiscrepancies(null)
    setExtraFileToDelete('')
    setHasEiarBrochure(null)

    if (!reportYear) {
      setStatusLoading(false)
      return () => {
        current = false
      }
    }

    setStatusLoading(true)

    getBrochurePreSignedUrl('EIAR', reportYear)
      .then(response => {
        if (!current) return

        if (response?.presignedUrl)
          setHasEiarBrochure(true)
        else if (response?.error === 'BROCHURE_NOT_FOUND')
          setHasEiarBrochure(false)
      })
      .catch(() => {})

    getNewEiarStatus(reportYear)
      .then(response => {
        if (!current) return
        if (response && !response.error) {
          const spreadsheetAlreadyExists = Boolean(response.spreadsheetExists)
          setSpreadsheetExists(spreadsheetAlreadyExists)
          setSpreadsheetUploaded(spreadsheetAlreadyExists)
          setPublishDate(response.publishDate || '')
        }
        else {
          setSpreadsheetError('There was an error loading the EIAR publication status.')
        }
      })
      .catch(() => {
        if (current)
          setSpreadsheetError('There was an error loading the EIAR publication status.')
      })
      .finally(() => {
        if (current)
          setStatusLoading(false)
      })

    return () => {
      current = false
    }
  }, [reportYear])

  useEffect(() => {
    if (newEiarFileUploadCount > 0) {
      setDiscrepancies(null)
      setSpreadsheetSuccess('')
    }
  }, [newEiarFileUploadCount])

  const handleSpreadsheetFile = useCallback((files) => {
    clearFileUploadResults()
    setSpreadsheetFile(files)
    setSpreadsheetError('')
    setSpreadsheetSuccess('')
    setDiscrepancies(null)
  }, [clearFileUploadResults])

  const uploadSpreadsheet = async () => {
    clearFileUploadResults()
    setShowSpreadsheetConfirmModal(false)
    setSpreadsheetSaving(true)
    setSpreadsheetError('')
    setSpreadsheetSuccess('')

    try {
      const response = await uploadSpreadsheetFile(spreadsheetFiles, reportYear)
      if (response && response.success) {
        setSpreadsheetExists(true)
        setPublishDate(response.publishDate || '')
        setSpreadsheetUploaded(true)
        setDiscrepancies(null)
        if (response.publishDate)
          setSpreadsheetSuccess(`EIAR reports for ${reportYear} were published successfully!`)
      }
      else if (response && response.error === 'NEW_EIAR_DISCREPANCIES') {
        setDiscrepancies(response)
        setSpreadsheetError('There are discrepancies you need to resolve before re-publishing this spreadsheet.')
      }
      else {
        setSpreadsheetError('There was an error saving the EIAR spreadsheet.')
      }
    }
    catch (error) {
      setSpreadsheetError(error.message || 'There was an error saving the EIAR spreadsheet.')
    }
    finally {
      setSpreadsheetSaving(false)
    }
  }

  const confirmSpreadsheetUpload = () => {
    clearFileUploadResults()
    try {
      getSpreadsheetYear(spreadsheetFiles[0], reportYear)
      setSpreadsheetError('')
      setShowSpreadsheetConfirmModal(true)
    }
    catch (error) {
      setSpreadsheetError(error.message || 'There was an error validating the EIAR spreadsheet.')
    }
  }

  const clearSpreadsheet = () => {
    clearFileUploadResults()
    setSpreadsheetFile([])
    setSpreadsheetError('')
    setSpreadsheetSuccess('')
    setSpreadsheetDropzoneKey(key => key + 1)
    setShowSpreadsheetConfirmModal(false)
  }

  const reuploadSpreadsheet = () => {
    clearSpreadsheet()
    setSpreadsheetUploaded(false)
  }

  const showDiscrepancies = useCallback(async () => {
    clearFileUploadResults()
    setSpreadsheetError('')
    setSpreadsheetSuccess('')
    try {
      const response = await checkNewEiarFiles(reportYear)
      if (response && !response.error) {
        setDiscrepancies(response)
        if (!hasBlockingDiscrepancies(response))
          setSpreadsheetSuccess('There are no discrepancies that block publish!')
      }
      else
        setSpreadsheetError('There was an error checking the EIAR files and spreadsheet.')
    }
    catch (error) {
      setSpreadsheetError(error.message || 'There was an error checking the EIAR files and spreadsheet.')
    }
  }, [clearFileUploadResults, reportYear])

  const publishSpreadsheet = async () => {
    clearFileUploadResults()
    setSpreadsheetError('')
    setSpreadsheetSuccess('')
    try {
      const response = await publishNewEiar(reportYear)
      if (response && response.success) {
        setPublishDate(response.publishDate)
        setDiscrepancies(null)
        setSpreadsheetUploaded(true)
        setSpreadsheetSuccess(`EIAR reports for ${reportYear} were published successfully!`)
      }
      else if (response && (response.error === 'NEW_EIAR_DISCREPANCIES' || response.error === 'NEW_EIAR_SPREADSHEET_REQUIRED')) {
        setDiscrepancies(response)
        setSpreadsheetError('There are discrepancies you need to resolve before publishing.')
      }
      else {
        setSpreadsheetError('There was an error publishing the EIAR reports.')
      }
    }
    catch (error) {
      setSpreadsheetError(error.message || 'There was an error publishing the EIAR reports.')
    }
  }

  const deleteExtraFile = async () => {
    const fileName = extraFileToDelete
    setExtraFileToDelete('')
    setSpreadsheetError('')
    setSpreadsheetSuccess('')

    try {
      const deleteResponse = await deleteNewEiarFile(reportYear, fileName)
      if (!deleteResponse || !deleteResponse.success) {
        setSpreadsheetError(`There was an error deleting ${fileName}.`)
        return
      }

      const comparison = await checkNewEiarFiles(reportYear)
      if (!comparison || comparison.error) {
        setSpreadsheetError('The file was deleted, but there was an error refreshing the discrepancies.')
        return
      }

      setDiscrepancies(comparison)
      if (!hasBlockingDiscrepancies(comparison))
        setSpreadsheetSuccess('There are no discrepancies that block publish!')
    }
    catch (error) {
      setSpreadsheetError(error.message || `There was an error deleting ${fileName}.`)
    }
  }

  const publicationControls = spreadsheetExists && (
    <div className={css.publicationActions}>
      <FilledYellow type="button" onClick={showDiscrepancies}>Show Discrepancies</FilledYellow>
      {!publishDate && <FilledBlue type="button" onClick={publishSpreadsheet}>Publish</FilledBlue>}
    </div>
  )

  const SpreadsheetActionButton = publishDate ? FilledBlue : FilledYellow

  return (
    <>
      <p><b>NOTE: This is the new system for uploading the EIAR spreadsheet and reports.
        To use the old system for a singles file EIAR report, select EIAR Full Donor Reports</b></p>

      <div className={css.reportYearSelector}>
        <label>Report Year:</label>
        <div className={css.reportYearDropdown}>
          <DropDownMenu
            className="input-wrap-dropdown"
            name="newEiarReportYear"
            title={reportYear || 'Select'}
            list={reportYearOptions}
            changeHandler={event => setReportYear(event.target.value === 'Select' ? '' : String(event.target.value))}
          />
        </div>
        {reportYear &&
          <div className={`${css.brochureAction} ${css.newEiarBrochureButton}`}>
            <BrochureUploadButton
              year={reportYear}
              type="EIAR"
              onUploadSuccess={() => setHasEiarBrochure(true)}
            />
            {hasEiarBrochure === false &&
              <span className={css.brochureWarning}>
                WARNING: No Endowment in Action brochure has been uploaded yet for {reportYear}.
              </span>}
          </div>}
      </div>

      {reportYear && <>
        {publishDate
          ? <>
              <p className={css.publishedSpreadsheetStatus}>
                The {reportYear} EIAR spreadsheet was published on {formatPublishDate(publishDate)}
              </p>
              {!spreadsheetUploaded &&
                <p>The spreadsheet must be a CSV file named: <b>"{'{Report Year}'} EIAR Spreadsheet.csv"</b> (without the curly braces).</p>}
            </>
          : <p>The spreadsheet must be a CSV file named: <b>"{'{Report Year}'} EIAR Spreadsheet.csv"</b> (without the curly braces).</p>}

        <div className={css.spreadsheetUploadSection}>
          {spreadsheetUploaded
            ? <div className={css.spreadsheetActions}>
                <FilledYellow type="button" onClick={reuploadSpreadsheet}>RE-UPLOAD SPREADSHEET</FilledYellow>
                {publicationControls}
              </div>
            : <>
                <p className={css.spreadsheetUploadLabel}>Use this File Dialog for the EIAR Spreadsheet:</p>
                <FileDropzone
                  key={'dropzone-spreadsheet-' + spreadsheetDropzoneKey}
                  dropzoneText="Drag and drop the EIAR Spreadsheet CSV file here"
                  setSelectedFiles={handleSpreadsheetFile}
                />
                <div className={css.spreadsheetActions}>
                  {publishDate &&
                    <FilledYellow
                      type="button"
                      onClick={clearSpreadsheet}
                      disabled={spreadsheetFiles.length === 0 && !spreadsheetError}
                    >
                      Clear
                    </FilledYellow>}
                  <SpreadsheetActionButton
                    type="button"
                    className={publishDate ? css.republishSpreadsheetButton : ''}
                    onClick={confirmSpreadsheetUpload}
                    disabled={spreadsheetFiles.length === 0 || spreadsheetSaving || statusLoading}
                  >
                    {spreadsheetSaving
                      ? (publishDate ? 'Re-Publishing Spreadsheet...' : 'Uploading Spreadsheet...')
                      : (publishDate ? 'Re-Publish Spreadsheet' : 'Upload Spreadsheet')}
                  </SpreadsheetActionButton>
                  {!publishDate &&
                    <FilledYellow
                      type="button"
                      onClick={clearSpreadsheet}
                      disabled={spreadsheetFiles.length === 0 && !spreadsheetError}
                    >
                      Clear
                    </FilledYellow>}
                  {publicationControls}
                </div>
              </>}
        </div>

        {spreadsheetError &&
          <Alert icon={iconError}>
            <b>{spreadsheetError}</b>
          </Alert>}

        {spreadsheetSuccess &&
          <Alert icon={successIcon} color={colors.messagingGreen}>
            <b>{spreadsheetSuccess}</b>
          </Alert>}

        {hasAnyDiscrepancies(discrepancies) &&
          <div className={css.discrepancyContainer}>
            <div className={css.discrepancyPanel}>
              {discrepancies.spreadsheetEntriesNotInFiles.length > 0 && <>
                <h4>{discrepancies.spreadsheetEntriesNotInFiles.length} Spreadsheet entries without files (blocks publish)</h4>
                <ul>{discrepancies.spreadsheetEntriesNotInFiles.map(fileName => <li key={fileName}>{fileName}</li>)}</ul>
              </>}
              {discrepancies.ambiguousSpreadsheetEntries.length > 0 && <>
                <h4>{discrepancies.ambiguousSpreadsheetEntries.length} Ambiguous spreadsheet entries (blocks publish)</h4>
                <ul>{discrepancies.ambiguousSpreadsheetEntries.map(entry => (
                  <li key={entry.spreadsheetEntry}>
                    <b>{entry.spreadsheetEntry}</b>: {entry.fileNames.join(', ')}
                  </li>
                ))}</ul>
              </>}
              {discrepancies.filesNotInSpreadsheet.length > 0 && <>
                <h4 className={css.nonBlockingDiscrepancyHeader}>
                  {discrepancies.filesNotInSpreadsheet.length} Files without spreadsheet entries (doesn't block publish)
                </h4>
                <ul>{discrepancies.filesNotInSpreadsheet.map(fileName => (
                  <li key={fileName} className={css.extraFileRow}>
                    <span>{fileName}</span>
                    <button
                      type="button"
                      className={css.deleteExtraFile}
                      title="Delete extra file"
                      aria-label={`Delete ${fileName}`}
                      onClick={() => {
                        clearFileUploadResults()
                        setExtraFileToDelete(fileName)
                      }}
                    >
                      <img src={deleteIcon} alt="" />
                    </button>
                  </li>
                ))}</ul>
              </>}
            </div>
            <button
              type="button"
              className={css.closeDiscrepancies}
              aria-label="Hide discrepancies"
              onClick={() => setDiscrepancies(null)}
            >
              <img src={closeIcon} alt="" />
            </button>
          </div>
        }

        <div className={css.reportFilesHeader}>
          <p className={css.spreadsheetUploadLabel}>Use this File Dialog for the EIAR Report files:</p>
        </div>

        {showSpreadsheetConfirmModal &&
          <ConfirmModal
            onConfirm={uploadSpreadsheet}
            onCancel={() => setShowSpreadsheetConfirmModal(false)}
            cancelText={"CANCEL"}
            confirmText={"OK"}
          >
            <div className={confirmModalCss.modalInner}>
              <div className={confirmModalCss.modalText}>
                {publishDate ? 'Re-publish' : 'Upload'} this spreadsheet and delete all previous EIAR entries for <strong>{reportYear}</strong>?
              </div>
            </div>
          </ConfirmModal>
        }

        {extraFileToDelete &&
          <ConfirmModal
            onConfirm={deleteExtraFile}
            onCancel={() => setExtraFileToDelete('')}
            cancelText={"CANCEL"}
            confirmText={"DELETE FILE"}
          >
            <div className={confirmModalCss.modalInner}>
              <div className={confirmModalCss.modalText}>
                Are you sure you want to delete the extra EIAR file:<br />
                <strong>{extraFileToDelete}</strong>?
              </div>
            </div>
          </ConfirmModal>
        }
      </>}
    </>
  )
}

export const validateFile = (file, errors, info) => {
  const fileNameParts = file.name.split('.');
  const fileExt = fileNameParts.pop();

  if (!fileExt || fileExt.toLowerCase() !== 'pdf') {
    errors.push({file, msg: "File must be a PDF"})
  }
  else {
    const fileBody = fileNameParts.join('.');
    const fileIdMatch = fileBody.match(/^([TLN]?\d+)/);
    if (!fileIdMatch) {
      info.message = "No CRM ID or Fund Number was found for the following files, they will be treated as one-off EIAR reports:";
      info.files.push({filename: file.name});
    }
  }
}
